import { Alert, Button, Card, CardBody, Input } from '@foundation/ui'
import { Link, createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import styles from './login.module.css'
import { api } from '@/lib/api'
import { isAuthenticated, setAuthState } from '@/lib/auth-store'

export const Route = createFileRoute('/login')({
  beforeLoad: () => {
    if (isAuthenticated()) {
      throw redirect({ to: '/dashboard' })
    }
  },
  component: LoginPage,
})

interface FormErrors {
  email?: string
  password?: string
}

function LoginPage() {
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [errors, setErrors] = useState<FormErrors>({})
  const [apiError, setApiError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {}

    if (!email) {
      newErrors.email = 'Email is required'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = 'Please enter a valid email'
    }

    if (!password) {
      newErrors.password = 'Password is required'
    } else if (password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault()
    setApiError(null)

    if (!validateForm()) return

    setIsLoading(true)

    api
      .login({ email, password })
      .then((response) => {
        setAuthState(response.data.user, response.data.accessToken, response.data.refreshToken)
        void navigate({ to: '/dashboard' })
      })
      .catch((err: unknown) => {
        setApiError(err instanceof Error ? err.message : 'Login failed')
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  return (
    <main className={styles.container}>
      <Card className={styles.card}>
        <CardBody>
          <div className={styles.logo}>
            <span className={styles.logoIcon}>&#10024;</span>
            <span className={styles.logoText}>ProofPulse</span>
          </div>
          <h2 className={styles.title}>Welcome back</h2>
          <p className={styles.subtitle}>Sign in to your account</p>

          {apiError && (
            <Alert variant="danger" className={styles.alert}>
              {apiError}
            </Alert>
          )}

          <form className={styles.form} onSubmit={handleSubmit}>
            <Input
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              autoComplete="email"
              {...(errors.email && { error: errors.email })}
              required
            />

            <Input
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              autoComplete="current-password"
              {...(errors.password && { error: errors.password })}
              required
            />
            <div className={styles.forgotPassword}>
              <Link to="/forgot-password">Forgot password?</Link>
            </div>

            <Button
              type="submit"
              variant="primary"
              loading={isLoading}
              className={styles.fullWidth}
            >
              Sign in
            </Button>
          </form>

          <p className={styles.footer}>
            Don&apos;t have an account? <Link to="/signup">Sign up</Link>
          </p>
        </CardBody>
      </Card>
    </main>
  )
}
